import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Tooltip, Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import EntityDetailDrawer from '../../EntityDetailDrawer';
import {
    ChangeSet, ChangeSetItem, ChangeSetRequestError, ChangeSetRow, DecisionResult,
    getChangeSet, listChangeSets, recomputeImpact, submitDecision,
} from '../../../api/ontologyChangeSetApi';
import ChangeSetItemCard from './ChangeSetItemCard';
import ChangeSetImpactPanel from './ChangeSetImpactPanel';
import {
    COLLECTION_LABEL, Choice, Choices, Edits, Notes, STATUS_COLOR, STATUS_LABEL,
    buildDecisionItems, canUserDecide, cascadeRejected, decisionSummary, formatTimestamp, groupByCollection,
    initialChoices, isDecided, itemLabel, reviewableItems, submitBlockers, takesEffectOn, writesRegulatoryTier,
} from './changeSetReview';
import {
    DIALOG_PAPER_SX, FIELD_SX, LABEL_SX, MONO_SX, Notice, Pill, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX, Section,
} from './changeSetUi';
import { dsVar, ink } from '../../../theme/tokens';

export const DRAWER_WIDTH = 760;

export interface ChangeSetReviewDrawerProps {
    changeSetId: string | null;
    onClose: () => void;
    /** A decision landed: the queue and the uploads list have moved. */
    onDecided?: () => void;
    /** Re-upload, offered when a change set failed or can no longer be resumed. */
    onIngest?: () => void;
}

/** The signed-in user, as the control plane will record them. Display only. */
const currentUserEmail = (): string | null => {
    try {
        return localStorage.getItem('user_email');
    } catch {
        return null;
    }
};

const HEADER_FIELDS: Array<{ key: string; label: string }> = [
    { key: 'versionLabel', label: 'Version' },
    { key: 'issuer', label: 'Issuer' },
    { key: 'documentIdentifier', label: 'Reference' },
    { key: 'effectiveDate', label: 'Effective' },
];

const provenanceText = (changeSet: ChangeSet, field: string): string => {
    const provenance = changeSet.identification?.fields?.[field];
    if (!provenance) return '';
    const from = provenance.source === 'caller' ? 'Entered at upload' : 'Read from the document';
    const evidence = (provenance.evidence ?? [])[0];
    const where = evidence?.pageNumber !== undefined ? `, p. ${evidence.pageNumber}` : '';
    const quote = evidence?.quote ? `: “${evidence.quote}”` : '';
    const confidence = provenance.confidence ? ` (${provenance.confidence} confidence)` : '';
    return `${from}${where}${confidence}${quote}`;
};

/** Spec §4.4 step 1: what the document is, and whether it is new or a revision. */
const ChangeSetHeader: React.FC<{ changeSet: ChangeSet; competing: ChangeSetRow[] }> = ({ changeSet, competing }) => {
    const doc = changeSet.sourceDocument ?? {};
    const match = changeSet.identification?.match;
    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Pill color={STATUS_COLOR[changeSet.status] ?? '#94A3B8'}>{STATUS_LABEL[changeSet.status] ?? changeSet.status}</Pill>
                <Pill color={changeSet.baseline ? '#60A5FA' : '#4ADE80'}>
                    {changeSet.baseline ? `Revision of ${changeSet.baseline.sourceDocCode}` : 'New document'}
                </Pill>
                {changeSet.scopeLevel === 'regulatory' && <Pill color="#C4B5FD">Regulatory</Pill>}
                {match?.method && match.method !== 'new' && (
                    <Pill color="#94A3B8">Matched by {match.method}{match.matchedTitle ? `: ${match.matchedTitle}` : ''}</Pill>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1.5 }}>
                {HEADER_FIELDS.map(({ key, label }) => {
                    const value = (doc as Record<string, any>)[key];
                    const provenance = provenanceText(changeSet, key);
                    return (
                        <Box key={key}>
                            <Typography sx={LABEL_SX}>{label}</Typography>
                            <Tooltip title={provenance} disableHoverListener={!provenance}>
                                <Typography sx={{
                                    fontSize: '0.8125rem', color: value ? dsVar('fg-body') : dsVar('fg-muted'),
                                    textDecoration: provenance ? `underline dotted ${dsVar('fg-faint')}` : 'none',
                                }}
                                >
                                    {value || '—'}
                                </Typography>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>

            {doc.summary && (
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.6, mt: 1.5 }}>{doc.summary}</Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                {(changeSet.identification?.possibleRevisionOf ?? []).map((candidate) => (
                    <Notice key={candidate.sourceDocCode} tone="info">
                        Is this a revision of <strong>{candidate.title || candidate.sourceDocCode}</strong>
                        {candidate.sourceDocCode && <Box component="span" sx={{ ...MONO_SX, ml: 0.5 }}>({candidate.sourceDocCode})</Box>}?
                        It was read as a new document. To link it, reject this change set and upload it again choosing
                        that document under “This revises…”.
                    </Notice>
                ))}
                {competing.length > 0 && (
                    <Notice tone="warning">
                        {competing.length === 1 ? 'Another change set' : `${competing.length} other change sets`} for{' '}
                        <Box component="span" sx={MONO_SX}>{changeSet.sourceDocCode}</Box> {competing.length === 1 ? 'is' : 'are'} also
                        awaiting review. Approving both would apply competing versions.
                    </Notice>
                )}
                {(changeSet.uncitedByRevision ?? []).length > 0 && (
                    <Notice tone="info">
                        No longer cited by this version, but still supported by other documents, so not proposed for retirement:{' '}
                        {(changeSet.uncitedByRevision ?? []).map((entry) => entry.code).join(', ')}.
                    </Notice>
                )}
            </Box>
        </Box>
    );
};

/** Spec §5.6: what the decision did. */
const ChangeSetOutcome: React.FC<{ changeSet: ChangeSet; result: DecisionResult | null }> = ({ changeSet, result }) => {
    const flagging = changeSet.flagging ?? result?.flagging ?? null;
    const notWritten = (changeSet.items ?? []).filter((item) => item.writeReport && item.writeReport.written === false);
    return (
        <Section title="Outcome">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body') }}>
                    {STATUS_LABEL[changeSet.status]}
                    {changeSet.reviewedBy && <> by <strong>{changeSet.reviewedBy}</strong></>}
                    {changeSet.reviewedAt && <> on {formatTimestamp(changeSet.reviewedAt)}</>}.
                    {result?.written !== undefined && ` ${result.written} of ${result.accepted ?? result.written} accepted element(s) written.`}
                </Typography>
                {changeSet.reviewNotes && (
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), fontStyle: 'italic' }}>“{changeSet.reviewNotes}”</Typography>
                )}
                {notWritten.length > 0 && (
                    <Notice tone="warning">
                        {notWritten.length} accepted item{notWritten.length === 1 ? ' was' : 's were'} not written:{' '}
                        {notWritten.map((item) => itemLabel(item)).join(', ')}. Each item below says why.
                    </Notice>
                )}
                {flagging && (
                    <Notice tone="info">
                        {flagging.flaggedReviews ?? 0} settled review{flagging.flaggedReviews === 1 ? '' : 's'},{' '}
                        {flagging.flaggedCapsuleRecords ?? 0} capsule record{flagging.flaggedCapsuleRecords === 1 ? '' : 's'} and{' '}
                        {flagging.flaggedCards ?? 0} alert card{flagging.flaggedCards === 1 ? '' : 's'} were flagged for revalidation
                        {flagging.minSeverity ? ` (severity ${flagging.minSeverity} and above)` : ''}.
                    </Notice>
                )}
            </Box>
        </Section>
    );
};

type ConfirmMode = 'submit' | 'reject_all' | null;

/**
 * Review one change set: what the document proposes, what it would affect, and the
 * decision. Docked beside the queue so the reviewer keeps their place in it.
 *
 * The decision is the approval. Accepted items are written to the ontology as approved,
 * attributed to the signed-in user, and the decisions they affect are flagged.
 */
const ChangeSetReviewDrawer: React.FC<ChangeSetReviewDrawerProps> = ({ changeSetId, onClose, onDecided, onIngest }) => {
    const [changeSet, setChangeSet] = useState<ChangeSet | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [competing, setCompeting] = useState<ChangeSetRow[]>([]);

    const [choices, setChoices] = useState<Choices>({});
    const [edits, setEdits] = useState<Edits>({});
    const [notes, setNotes] = useState<Notes>({});
    const [reviewNotes, setReviewNotes] = useState('');
    const [showUnchanged, setShowUnchanged] = useState(false);

    const [confirm, setConfirm] = useState<ConfirmMode>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [result, setResult] = useState<DecisionResult | null>(null);
    const [refreshingImpact, setRefreshingImpact] = useState(false);

    const userEmail = currentUserEmail();

    /** Fetch the change set once per open; the impact sample can be thousands of rows. */
    const load = useCallback(async (id: string, { reset }: { reset: boolean }, signal?: AbortSignal) => {
        setLoading(true);
        setLoadError(null);
        try {
            const fresh = await getChangeSet(id, { signal });
            setChangeSet(fresh);
            if (reset) {
                setChoices(fresh.status === 'pending_review' ? initialChoices(fresh.items) : {});
                setEdits({});
                setNotes({});
                setReviewNotes('');
                setResult(null);
                setSubmitError(null);
            }
            return fresh;
        } catch (error) {
            if (signal?.aborted) return null;
            setLoadError(error instanceof ChangeSetRequestError ? error.message : 'The change set could not be loaded.');
            return null;
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, []);

    useEffect(() => {
        setChangeSet(null);
        setCompeting([]);
        if (!changeSetId) return undefined;
        const controller = new AbortController();
        load(changeSetId, { reset: true }, controller.signal);
        return () => controller.abort();
    }, [changeSetId, load]);

    // Other change sets awaiting review for the same document (spec §7).
    const sourceDocCode = changeSet?.sourceDocCode;
    useEffect(() => {
        if (!changeSetId || !sourceDocCode || changeSet?.status !== 'pending_review') return undefined;
        const controller = new AbortController();
        listChangeSets({ status: 'pending_review', limit: 500 }, { signal: controller.signal })
            .then((rows) => setCompeting(rows.filter((row) => row.changeSetId !== changeSetId && row.sourceDocCode === sourceDocCode)))
            .catch(() => setCompeting([]));
        return () => controller.abort();
    }, [changeSetId, sourceDocCode, changeSet?.status]);

    const items = useMemo(() => changeSet?.items ?? [], [changeSet]);
    const reviewable = useMemo(() => reviewableItems(items), [items]);
    const unchanged = useMemo(() => items.filter((item) => item.changeType === 'unchanged'), [items]);
    const cascaded = useMemo(() => cascadeRejected(items, choices), [items, choices]);
    const labelById = useMemo(() => new Map(items.map((item) => [item.itemId, itemLabel(item)])), [items]);

    const pending = changeSet?.status === 'pending_review';
    const userCanDecide = changeSet ? canUserDecide(changeSet, userEmail) : false;
    const editable = pending && userCanDecide && !submitting;
    const blockers = changeSet && pending ? submitBlockers(changeSet, choices, edits, userEmail) : [];

    const setChoice = (item: ChangeSetItem, choice: Choice | undefined) =>
        setChoices((current) => ({ ...current, [item.itemId]: choice }));

    const refreshImpact = async () => {
        if (!changeSet) return;
        setRefreshingImpact(true);
        try {
            const impact = await recomputeImpact(changeSet.changeSetId);
            setChangeSet((current) => (current ? { ...current, impact } : current));
        } catch (error) {
            setSubmitError(error instanceof ChangeSetRequestError ? error.message : 'Impact could not be recomputed.');
        } finally {
            setRefreshingImpact(false);
        }
    };

    /**
     * Submit once — never retried. While the call runs the change set is `resolving` and a
     * second call gets a 409, so any failure re-reads the change set instead.
     */
    const submit = async (mode: Exclude<ConfirmMode, null>) => {
        if (!changeSet) return;
        setSubmitting(true);
        setSubmitError(null);

        // Re-read just before submitting: someone else may have decided it meanwhile.
        const latest = await load(changeSet.changeSetId, { reset: false });
        if (!latest || latest.status !== 'pending_review') {
            setSubmitting(false);
            setConfirm(null);
            setSubmitError('This change set is no longer awaiting review. It is shown as it now stands.');
            return;
        }

        const allRejected: Choices = Object.fromEntries(reviewable.map((item) => [item.itemId, 'reject' as Choice]));
        const decisionItems = mode === 'reject_all'
            ? buildDecisionItems(items, allRejected, {}, {})
            : buildDecisionItems(items, choices, edits, notes);

        try {
            const outcome = await submitDecision(changeSet.changeSetId, { review_notes: reviewNotes.trim(), items: decisionItems });
            setResult(outcome);
            setConfirm(null);
            await load(changeSet.changeSetId, { reset: false });
            onDecided?.();
        } catch (error) {
            const status = error instanceof ChangeSetRequestError ? error.status : null;
            const message = error instanceof ChangeSetRequestError ? error.message : 'The decision could not be submitted.';
            setConfirm(null);
            if (status === 410) {
                setSubmitError('This review can no longer be resumed. Re-upload the document.');
            } else if (status === 503) {
                setSubmitError('The review service is unavailable. Try again later.');
            } else if (status === 400 || status === 403) {
                // The form is kept: the message names the item(s) to fix.
                setSubmitError(message);
            } else {
                // 409, a timeout or an unexpected error: what happened is on the change set.
                setSubmitError(`${message} The change set has been re-read and is shown as it now stands.`);
                await load(changeSet.changeSetId, { reset: false });
                onDecided?.();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const summary = useMemo(() => decisionSummary(items, choices), [items, choices]);
    const doc = changeSet?.sourceDocument ?? {};
    const title = doc.title || changeSet?.sourceDocCode || 'Change set';

    const footer = changeSet && pending && userCanDecide ? (
        <Box>
            <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="Review notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                disabled={submitting}
                sx={{ ...FIELD_SX, mb: 1.25 }}
            />
            {blockers.length > 0 && (
                <Box component="ul" data-testid="submit-blockers" sx={{ m: 0, mb: 1.25, pl: 2.5, color: dsVar('fg-2'), fontSize: '0.75rem', lineHeight: 1.7 }}>
                    {blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
                </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                <Button onClick={() => setConfirm('reject_all')} disabled={submitting} sx={{ ...SECONDARY_BUTTON_SX, color: ink('#F87171') }}>
                    Reject whole change set
                </Button>
                <Button onClick={() => setConfirm('submit')} disabled={submitting || blockers.length > 0} sx={PRIMARY_BUTTON_SX}>
                    {submitting ? 'Applying…' : 'Submit decision'}
                </Button>
            </Box>
        </Box>
    ) : changeSet?.status === 'failed' && onIngest ? (
        <Button onClick={onIngest} sx={PRIMARY_BUTTON_SX}>Upload the document again</Button>
    ) : undefined;

    const renderItem = (item: ChangeSetItem) => (
        <ChangeSetItemCard
            key={item.itemId}
            item={item}
            sourceLink={doc.sourceLink}
            readOnly={!editable || item.changeType === 'unchanged'}
            choice={choices[item.itemId]}
            onChoice={(choice) => setChoice(item, choice)}
            edits={edits[item.itemId]}
            onEdits={(next) => setEdits((current) => ({ ...current, [item.itemId]: next }))}
            note={notes[item.itemId]}
            onNote={(note) => setNotes((current) => ({ ...current, [item.itemId]: note }))}
            cascadeCauses={cascaded.get(item.itemId)?.map((id) => labelById.get(id) ?? id)}
            // A calendar date (YYYY-MM-DD), shown as written: converting it through a
            // local-time Date could move it a day.
            takesEffectOn={changeSet && item.writeReport?.written ? takesEffectOn(item, changeSet) : null}
        />
    );

    return (
        <>
            <EntityDetailDrawer
                open={Boolean(changeSetId)}
                onClose={onClose}
                docked
                dockedMinWidth={1280}
                width={DRAWER_WIDTH}
                icon={<FactCheckOutlinedIcon />}
                subtitle={changeSet ? `Change set · ${changeSet.changeSetId.slice(0, 8)}` : 'Change set'}
                title={title}
                description=""
                actions={footer}
            >
                {loading && !changeSet && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                        <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                    </Box>
                )}
                {loadError && <Notice tone="error">{loadError}</Notice>}

                {changeSet && (
                    <Box data-testid="change-set-review">
                        <ChangeSetHeader changeSet={changeSet} competing={competing} />

                        {submitError && <Box sx={{ mt: 2 }}><Notice tone="error">{submitError}</Notice></Box>}

                        {changeSet.status === 'failed' && (
                            <Box sx={{ mt: 2 }}><Notice tone="error">{changeSet.failureReason || 'Reading the document failed.'}</Notice></Box>
                        )}
                        {(changeSet.status === 'extracting' || changeSet.status === 'proposed') && (
                            <Box sx={{ mt: 2 }}>
                                <Notice tone="info">
                                    {changeSet.status === 'extracting'
                                        ? 'The document is still being read. This takes a few minutes.'
                                        : 'The proposal is being prepared for review.'}
                                </Notice>
                            </Box>
                        )}
                        {changeSet.status === 'resolving' && (
                            <Box sx={{ mt: 2 }}><Notice tone="info">A decision is being applied.</Notice></Box>
                        )}
                        {pending && !userCanDecide && (
                            <Box sx={{ mt: 2 }}>
                                <Notice tone="warning">
                                    Awaiting a regulatory approver: {(changeSet.regulatoryApprovers ?? []).join(', ') || 'none is configured'}.
                                </Notice>
                            </Box>
                        )}
                        {pending && writesRegulatoryTier(changeSet) && userCanDecide && (
                            <Box sx={{ mt: 2 }}>
                                <Notice tone="warning">This change set writes regulatory-tier elements, which every organisation resolves.</Notice>
                            </Box>
                        )}

                        {(isDecided(changeSet.status) || changeSet.status === 'failed') && changeSet.reviewedAt && (
                            <ChangeSetOutcome changeSet={changeSet} result={result} />
                        )}

                        {isDecided(changeSet.status) && changeSet.appliedImpact ? (
                            <ChangeSetImpactPanel impact={changeSet.appliedImpact} title="What was flagged" verb="were" />
                        ) : (
                            <ChangeSetImpactPanel
                                impact={changeSet.impact}
                                onRefresh={pending || changeSet.status === 'proposed' ? refreshImpact : undefined}
                                refreshing={refreshingImpact}
                            />
                        )}

                        {groupByCollection(reviewable).map(([collection, group]) => (
                            <Section key={collection} title={`${COLLECTION_LABEL[collection]} (${group.length})`}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{group.map(renderItem)}</Box>
                            </Section>
                        ))}

                        {changeSet.noChanges && (
                            <Box sx={{ mt: 2 }}>
                                <Notice tone="info">The document proposes nothing that differs from what is in force.</Notice>
                            </Box>
                        )}

                        {unchanged.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Button size="small" onClick={() => setShowUnchanged((v) => !v)} sx={{ textTransform: 'none', color: dsVar('fg-2'), px: 0 }}>
                                    {showUnchanged ? 'Hide' : 'Show'} {unchanged.length} element{unchanged.length === 1 ? '' : 's'} mentioned, unchanged
                                </Button>
                                {showUnchanged && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 1 }}>{unchanged.map(renderItem)}</Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </EntityDetailDrawer>

            {/* Spec §5.5 step 7: say exactly what will be written before it is. */}
            <Dialog open={confirm !== null} onClose={submitting ? undefined : () => setConfirm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
                <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, color: dsVar('fg') }}>
                    {confirm === 'reject_all' ? 'Reject the whole change set?' : 'Submit this decision?'}
                </DialogTitle>
                <DialogContent>
                    {confirm === 'reject_all' ? (
                        <>
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mb: 1.5, lineHeight: 1.6 }}>
                                Nothing is written. The same file can then be uploaded again.
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                label="Reason (required)"
                                value={reviewNotes}
                                onChange={(e) => setReviewNotes(e.target.value)}
                                disabled={submitting}
                                sx={FIELD_SX}
                            />
                        </>
                    ) : (
                        <Box sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), lineHeight: 1.7 }}>
                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mb: 1.5 }}>
                                Accepted and edited items are written to the library as approved, with you recorded as the approver.
                                Case decisions they affect are flagged for revalidation.
                            </Typography>
                            {([
                                ['Accept', summary.accepted, '#4ADE80'],
                                ['Accept with edits', summary.edited, '#60A5FA'],
                                ['Reject', summary.rejected, '#F87171'],
                                ['Rejected because a dependency is rejected', summary.cascaded, '#FCA5A5'],
                            ] as Array<[string, ChangeSetItem[], string]>).filter(([, group]) => group.length > 0).map(([label, group, color]) => (
                                <Box key={label} sx={{ mb: 1 }}>
                                    <Typography sx={{ ...LABEL_SX, color }}>{label} ({group.length})</Typography>
                                    {group.map((item) => itemLabel(item)).join(', ')}
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setConfirm(null)} disabled={submitting} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
                    <Button
                        onClick={() => confirm && submit(confirm)}
                        disabled={submitting || (confirm === 'reject_all' && !reviewNotes.trim())}
                        sx={PRIMARY_BUTTON_SX}
                    >
                        {submitting ? 'Applying…' : confirm === 'reject_all' ? 'Reject change set' : 'Submit decision'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ChangeSetReviewDrawer;
